import {
  RECURRING_FREQUENCIES,
  type RecurringDonationSeries,
  type RecurringEndCondition,
  type RecurringFrequency,
} from '@/types/recurringDonation'

function isFrequency(value: unknown): value is RecurringFrequency {
  return RECURRING_FREQUENCIES.includes(value as RecurringFrequency)
}

function mapEndCondition(value: unknown): RecurringEndCondition {
  if (!value || typeof value !== 'object') return { type: 'until_stopped' }
  const row = value as Record<string, unknown>
  if (row.type === 'on_date' && typeof row.endDate === 'string') {
    return { type: 'on_date', endDate: row.endDate }
  }
  if (row.type === 'after_occurrences') {
    const occurrenceCount = Number(row.occurrenceCount)
    if (Number.isInteger(occurrenceCount) && occurrenceCount >= 1) {
      return { type: 'after_occurrences', occurrenceCount }
    }
  }
  return { type: 'until_stopped' }
}

/**
 * Maps a `GET /me/donations/recurring` series object.
 *
 * Series routes speak `amountPence` only. A legacy `amount` field is ignored
 * so a stale alias cannot be shown as the schedule amount.
 */
export function mapRecurringSeries(
  row: Record<string, unknown>
): RecurringDonationSeries {
  const lifecycle = Array.isArray(row.lifecycle)
    ? row.lifecycle.flatMap((event) => {
        if (!event || typeof event !== 'object') return []
        const item = event as Record<string, unknown>
        if (
          item.type !== 'started' &&
          item.type !== 'stopped' &&
          item.type !== 'restarted' &&
          item.type !== 'completed'
        ) {
          return []
        }
        return [
          {
            type: item.type as RecurringDonationSeries['lifecycle'][number]['type'],
            at: String(item.at ?? ''),
          },
        ]
      })
    : []

  const exceptions = Array.isArray(row.exceptions)
    ? row.exceptions.flatMap((event) => {
        if (!event || typeof event !== 'object') return []
        const item = event as Record<string, unknown>
        if (item.kind !== 'skipped' && item.kind !== 'deleted') return []
        return [
          {
            occurrenceDate: String(item.occurrenceDate ?? ''),
            kind: item.kind as RecurringDonationSeries['exceptions'][number]['kind'],
          },
        ]
      })
    : []

  const amountPence = Number(row.amountPence)

  return {
    id: String(row.id ?? ''),
    charity: String(row.charity ?? ''),
    amountPence: Number.isFinite(amountPence) ? amountPence : 0,
    giftAidDeclared: row.giftAidDeclared === true,
    notes: typeof row.notes === 'string' ? row.notes : undefined,
    frequency: isFrequency(row.frequency) ? row.frequency : 'monthly',
    firstDonationDate: String(row.firstDonationDate ?? ''),
    scheduleAnchorDate: String(
      row.scheduleAnchorDate ?? row.firstDonationDate ?? ''
    ),
    nextDonationDate:
      typeof row.nextDonationDate === 'string' ? row.nextDonationDate : null,
    endCondition: mapEndCondition(row.endCondition),
    status:
      row.status === 'stopped' || row.status === 'completed'
        ? row.status
        : 'active',
    startedAt: String(row.startedAt ?? ''),
    stoppedAt: typeof row.stoppedAt === 'string' ? row.stoppedAt : undefined,
    completedAt:
      typeof row.completedAt === 'string' ? row.completedAt : undefined,
    lifecycle,
    exceptions,
  }
}
